import i18n from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'
import { en } from './locales/en'
import { ru } from './locales/ru'

// Порядок задаёт и перебор в переключателе языка.
export const SUPPORTED_LOCALES = ['ru', 'en'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

// Эндонимы — в переключателе не переводятся.
export const LOCALE_NAMES: Record<Locale, string> = {
  ru: 'Русский',
  en: 'English',
}

const FALLBACK_LOCALE: Locale = 'en'
// Выбор per-device, как podpipe.playback_rate.
const STORAGE_KEY = 'podpipe.locale'

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

function loadLocale(): Locale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return isLocale(value) ? value : null
  } catch {
    return null
  }
}

function saveLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // приватный режим — выбор проживёт до перезагрузки
  }
}

// Сохранённый выбор → первое совпадение в navigator.languages по основному сабтегу
// (ru-RU → ru, en-GB → en) → en.
function detectLocale(): Locale {
  const saved = loadLocale()
  if (saved) return saved
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const tag of preferred) {
    const primary = tag?.split('-')[0].toLowerCase()
    if (isLocale(primary)) return primary
  }
  return FALLBACK_LOCALE
}

// Подписка до init: init синхронно эмитит languageChanged, и <html lang> встаёт до первого рендера.
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng
})

// Обе локали статически в бандле (несколько КБ gzip): без Suspense, без мигания
// непереведённого текста, precache service worker'а не меняется. Ленивая загрузка
// словарей через import() понадобится только с третьим языком.
void i18n.use(initReactI18next).init({
  resources: {
    ru: { translation: ru },
    en: { translation: en },
  },
  lng: detectLocale(),
  fallbackLng: FALLBACK_LOCALE,
  supportedLngs: SUPPORTED_LOCALES,
  initAsync: false,
  enableSelector: true,
  // React экранирует сам.
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
})

// Текущая локаль для кода вне React (Intl, правила показа ошибок).
export function getLocale(): Locale {
  const current = i18n.resolvedLanguage
  return isLocale(current) ? current : FALLBACK_LOCALE
}

// Смена на лету, без перезагрузки: useTranslation-компоненты перерисуются сами.
export function setLocale(locale: Locale): void {
  saveLocale(locale)
  void i18n.changeLanguage(locale)
}

// Текущая локаль с подпиской на её смену.
export function useLocale(): Locale {
  const { i18n: instance } = useTranslation()
  const current = instance.resolvedLanguage
  return isLocale(current) ? current : FALLBACK_LOCALE
}

export { i18n }
